import type { Employee } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  COMP_DISCIPLINES,
  DEPARTMENTS,
  EMPLOYEE_CURRENCIES,
  EMPLOYEE_STATUSES,
  EMPLOYMENT_TYPES,
  JOB_LEVELS,
  WORK_TYPES,
} from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";

interface DataUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type UploadStep = "upload" | "validating" | "preview" | "errors" | "submitting" | "success";

interface ValidationError {
  row: number;
  field: string;
  value: string;
  message: string;
}

interface ParsedRow {
  [key: string]: string | undefined;
}

const COLUMN_MAP: Record<string, string> = {
  nome: "fullName",
  "nome completo": "fullName",
  cargo: "jobTitle",
  "disciplina comp": "compDiscipline",
  nivel: "jobLevel",
  nível: "jobLevel",
  departamento: "department",
  gestor: "directManagerName",
  status: "status",
  salario: "currentSalary",
  salário: "currentSalary",
  moeda: "currency",
  currency: "currency",
  "tabela salarial": "salaryTableId",
  "tipo contrato": "employmentType",
  "data inicio": "startDate",
  "data início": "startDate",
  "data fim": "endDate",
  "modelo trabalho": "workType",
  "email corporativo": "corporateEmail",
  "email pessoal": "personalEmail",
  telefone: "phone",
  cpf: "cpf",
  "data nascimento": "birthdate",
  "data de nascimento": "birthdate",
  endereco: "address",
  endereço: "address",
  fullname: "fullName",
  jobtitle: "jobTitle",
  compdiscipline: "compDiscipline",
  joblevel: "jobLevel",
  department: "department",
  directmanagername: "directManagerName",
  directmanagerid: "directManagerName",
  currentsalary: "currentSalary",
  employmenttype: "employmentType",
  startdate: "startDate",
  enddate: "endDate",
  worktype: "workType",
  corporateemail: "corporateEmail",
  personalemail: "personalEmail",
  phone: "phone",
  birthdate: "birthdate",
  address: "address",
  salarytableid: "salaryTableId",
};

function buildLookup(entries: readonly { value: string; label: string }[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of entries) {
    map.set(entry.value.toLowerCase(), entry.label);
    map.set(entry.label.toLowerCase(), entry.label);
  }
  return map;
}

const CONSTRAINED_FIELDS: Record<string, { lookup: Map<string, string>; validValues: string[] }> = {
  jobLevel: {
    lookup: buildLookup(JOB_LEVELS),
    validValues: JOB_LEVELS.map((j) => j.label),
  },
  department: {
    lookup: buildLookup(DEPARTMENTS),
    validValues: DEPARTMENTS.map((d) => d.label),
  },
  employmentType: {
    lookup: buildLookup(EMPLOYMENT_TYPES),
    validValues: EMPLOYMENT_TYPES.map((e) => e.label),
  },
  workType: {
    lookup: buildLookup(WORK_TYPES),
    validValues: WORK_TYPES.map((w) => w.label),
  },
  status: {
    lookup: buildLookup(EMPLOYEE_STATUSES),
    validValues: EMPLOYEE_STATUSES.map((s) => s.label),
  },
  compDiscipline: {
    lookup: buildLookup(COMP_DISCIPLINES),
    validValues: COMP_DISCIPLINES.map((c) => c.label),
  },
  currency: {
    lookup: buildLookup(EMPLOYEE_CURRENCIES),
    validValues: EMPLOYEE_CURRENCIES.map((c) => c.label),
  },
};

function normalizeColumnName(header: string): string | undefined {
  const cleaned = header
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, " ");
  return COLUMN_MAP[cleaned] || COLUMN_MAP[cleaned.replace(/\s/g, "")];
}

function parseSpreadsheetData(data: unknown[][]): ParsedRow[] {
  if (data.length < 2) return [];
  const headers = data[0].map((h) => String(h ?? "").trim());
  const mappedHeaders = headers.map((h) => normalizeColumnName(h));

  const rows: ParsedRow[] = [];
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    const obj: ParsedRow = {};
    let hasData = false;

    for (let j = 0; j < headers.length; j++) {
      const field = mappedHeaders[j];
      if (!field) continue;
      const cellValue = row[j];
      if (cellValue !== null && cellValue !== undefined && String(cellValue).trim() !== "") {
        obj[field] = String(cellValue).trim();
        hasData = true;
      }
    }

    if (hasData) rows.push(obj);
  }
  return rows;
}

function validateRowsLocally(rows: ParsedRow[], employees: Employee[]): ValidationError[] {
  const errors: ValidationError[] = [];
  const employeeNames = new Set(employees.map((e) => e.fullName.toLowerCase().trim()));
  const employeeIds = new Set(employees.map((e) => e.id));

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 1;

    if (!row.fullName?.trim()) {
      errors.push({
        row: rowNum,
        field: "Nome",
        value: row.fullName ?? "",
        message: "Nome é obrigatório",
      });
      continue;
    }

    for (const [field, config] of Object.entries(CONSTRAINED_FIELDS)) {
      const value = row[field];
      if (!value) continue;
      const resolved = config.lookup.get(value.toLowerCase().trim());
      if (!resolved) {
        const fieldLabels: Record<string, string> = {
          jobLevel: "Nível",
          department: "Departamento",
          employmentType: "Tipo Contrato",
          workType: "Modelo Trabalho",
          status: "Status",
          compDiscipline: "Disciplina Comp",
        };
        errors.push({
          row: rowNum,
          field: fieldLabels[field] || field,
          value,
          message: `Valor inválido. Esperado: ${config.validValues.slice(0, 5).join(", ")}${config.validValues.length > 5 ? "..." : ""}`,
        });
      }
    }

    if (row.directManagerName) {
      const name = row.directManagerName.trim();
      if (!employeeIds.has(name) && !employeeNames.has(name.toLowerCase())) {
        errors.push({
          row: rowNum,
          field: "Gestor",
          value: name,
          message: "Nenhum colaborador encontrado com este nome",
        });
      }
    }

    const dateFieldLabels: Record<string, string> = {
      startDate: "Data Início",
      endDate: "Data Fim",
      birthdate: "Data Nascimento",
    };
    for (const [field, label] of Object.entries(dateFieldLabels)) {
      const val = row[field];
      if (val && Number.isNaN(new Date(val).getTime())) {
        errors.push({
          row: rowNum,
          field: label,
          value: val,
          message: `Formato de data inválido. Use YYYY-MM-DD ou DD/MM/YYYY.`,
        });
      }
    }
  }

  return errors;
}

export function DataUploadDialog({ open, onOpenChange }: DataUploadDialogProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<UploadStep>("upload");
  const [fileName, setFileName] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [uploadResult, setUploadResult] = useState<{
    created: number;
    updated: number;
    skippedErrors?: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { data: employeesData = [] } = useQuery<Employee[]>({
    ...orpc.employees.list.queryOptions(),
    enabled: open,
  });

  const uploadMutation = useMutation({
    ...orpc.employees.bulkUpload.mutationOptions(),
    onSuccess: (data) => {
      setUploadResult({
        created: data.created,
        updated: data.updated,
        skippedErrors: data.skippedErrors?.length,
      });
      setStep("success");
      queryClient.invalidateQueries({ queryKey: orpc.employees.list.queryOptions().queryKey });
    },
    onError: (error: any) => {
      const serverErrors = error?.data?.errors;
      if (Array.isArray(serverErrors) && serverErrors.length > 0) {
        setValidationErrors(serverErrors);
        setStep("errors");
      } else {
        toast({
          variant: "destructive",
          description: error.message || "Falha ao enviar planilha. Tente novamente.",
        });
        setStep("preview");
      }
    },
  });

  const resetState = useCallback(() => {
    setStep("upload");
    setFileName(null);
    setParsedRows([]);
    setValidationErrors([]);
    setUploadResult(null);
    setIsDragging(false);
  }, []);

  const processFile = useCallback(
    (file: File) => {
      const validTypes = [
        "text/csv",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const isValidExt = /\.(csv|xlsx|xls)$/i.test(file.name);
      if (!validTypes.includes(file.type) && !isValidExt) {
        toast({
          variant: "destructive",
          description: "Formato inválido. Use arquivos .csv, .xlsx ou .xls.",
        });
        return;
      }

      setFileName(file.name);
      setStep("validating");

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            defval: "",
          }) as unknown[][];

          const rows = parseSpreadsheetData(jsonData);
          if (rows.length === 0) {
            toast({
              variant: "destructive",
              description: "Planilha vazia. Adicione pelo menos uma linha de dados.",
            });
            resetState();
            return;
          }

          setParsedRows(rows);

          const errors = validateRowsLocally(rows, employeesData);
          if (errors.length > 0) {
            setValidationErrors(errors);
            setStep("errors");
          } else {
            setStep("preview");
          }
        } catch {
          toast({
            variant: "destructive",
            description:
              "Não foi possível processar a planilha. Verifique o arquivo e tente de novo.",
          });
          resetState();
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [employeesData, toast, resetState],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    [processFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processFile(file);
      if (e.target) e.target.value = "";
    },
    [processFile],
  );

  const handleSubmit = useCallback(
    (ignoreErrors = false) => {
      setStep("submitting");
      uploadMutation.mutate({
        rows: parsedRows as Record<string, unknown>[],
        ignoreErrors,
      });
    },
    [parsedRows, uploadMutation],
  );

  const handleClose = useCallback(
    (openState: boolean) => {
      if (!openState) resetState();
      onOpenChange(openState);
    },
    [onOpenChange, resetState],
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="glass-modal sm:max-w-[680px]"
        overlayClassName="glass-scrim"
        data-testid="dialog-data-upload"
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle>Importar planilha de colaboradores</DialogTitle>
            <span className="caption-mono text-[hsl(var(--ink-muted))]">Esc para fechar</span>
          </div>
          <DialogDescription>
            Aceita .csv, .xlsx ou .xls. Cada linha precisa de nome; demais campos são opcionais.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="py-2">
            {/* biome-ignore lint/a11y/noStaticElementInteractions: dropzone uses drag-and-drop interaction */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: file input handles keyboard access */}
            <div
              className={`flex flex-col items-center justify-center rounded-xl border border-dashed p-10 transition-colors cursor-pointer ${
                isDragging
                  ? "border-[hsl(var(--comp-coral))] bg-[hsl(var(--comp-coral))]/5"
                  : "border-[hsl(var(--card-line))] hover:border-[hsl(var(--ink-muted))]"
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              data-testid="dropzone-upload"
            >
              <p className="body-strong text-[hsl(var(--ink-primary))]">
                Arraste a planilha aqui ou clique para selecionar.
              </p>
              <p className="caption-mono mt-2">CSV · XLSX · XLS</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="input-file-upload"
            />
          </div>
        )}

        {step === "validating" && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <p className="body-strong text-[hsl(var(--ink-primary))]">Validando planilha…</p>
          </div>
        )}

        {step === "preview" && (
          <div className="py-2 space-y-4">
            <div className="flex items-center gap-3 py-2 border-b border-[hsl(var(--card-line))]">
              <div className="flex-1 min-w-0">
                <p className="body-strong text-[hsl(var(--ink-primary))] truncate">{fileName}</p>
                <p className="caption-mono">
                  <span className="num-mono">{parsedRows.length}</span> colaborador
                  {parsedRows.length === 1 ? "" : "es"} encontrado
                  {parsedRows.length === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                className="text-sm underline text-[hsl(var(--ink-muted))] hover:text-[hsl(var(--ink-primary))]"
                onClick={resetState}
                data-testid="button-remove-file"
              >
                Trocar arquivo
              </button>
            </div>

            <div className="max-h-[300px] overflow-auto border-y border-[hsl(var(--card-line))]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[hsl(var(--paper-white))]">
                  <tr className="border-b border-[hsl(var(--card-line))]">
                    <th className="text-left px-3 py-2 caption-mono">#</th>
                    <th className="text-left px-3 py-2 caption-mono">Nome</th>
                    <th className="text-left px-3 py-2 caption-mono">E-mail corp.</th>
                    <th className="text-left px-3 py-2 caption-mono">Cargo</th>
                    <th className="text-left px-3 py-2 caption-mono">Depto.</th>
                    <th className="text-left px-3 py-2 caption-mono">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedRows.slice(0, 50).map((row, i) => (
                    <tr
                      // biome-ignore lint/suspicious/noArrayIndexKey: static preview list
                      key={`preview-${row.fullName}-${i}`}
                      className="border-b border-[hsl(var(--card-line))]"
                    >
                      <td className="px-3 py-1.5 num-mono text-[hsl(var(--ink-muted))]">{i + 1}</td>
                      <td className="px-3 py-1.5">{row.fullName || "—"}</td>
                      <td className="px-3 py-1.5">{row.corporateEmail || "—"}</td>
                      <td className="px-3 py-1.5">{row.jobTitle || "—"}</td>
                      <td className="px-3 py-1.5">{row.department || "—"}</td>
                      <td className="px-3 py-1.5">
                        <span className="body-strong text-[hsl(var(--forest-pulse))]">Pronta</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedRows.length > 50 && (
                <p className="caption-mono p-2 text-center">
                  … e mais <span className="num-mono">{parsedRows.length - 50}</span> linhas
                </p>
              )}
            </div>

            <p className="text-sm">
              <span className="body-strong text-[hsl(var(--forest-pulse))]">
                Todas as linhas passaram na validação.
              </span>
            </p>
          </div>
        )}

        {step === "errors" && (
          <div className="py-2 space-y-4">
            <div className="flex items-center gap-3 py-2 border-b border-[hsl(var(--card-line))]">
              <div className="flex-1 min-w-0">
                <p className="body-strong text-[hsl(var(--ink-primary))] truncate">{fileName}</p>
                <p className="caption-mono">
                  <span className="num-mono">{parsedRows.length}</span> linha
                  {parsedRows.length === 1 ? "" : "s"} ·{" "}
                  <span className="body-strong text-[hsl(var(--alert-red))]">
                    <span className="num-mono">{validationErrors.length}</span> com inconsistência
                  </span>
                </p>
              </div>
            </div>

            <ul className="space-y-1.5 max-h-[300px] overflow-auto text-sm">
              {validationErrors.map((err, i) => (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: static error list
                  key={`err-${err.row}-${err.field}-${i}`}
                  className="text-[hsl(var(--ink-primary))]"
                >
                  <span className="caption-mono mr-2">
                    Linha <span className="num-mono">{err.row}</span>
                  </span>
                  <span className="body-strong text-[hsl(var(--alert-red))]">
                    {err.field} inválido
                  </span>
                  {err.value ? (
                    <>
                      {" "}
                      — valor <span className="num-mono">"{err.value}"</span>
                    </>
                  ) : null}
                  . {err.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === "submitting" && (
          <div className="flex flex-col items-center justify-center py-10 gap-2">
            <p className="body-strong text-[hsl(var(--ink-primary))]">Enviando dados…</p>
          </div>
        )}

        {step === "success" && uploadResult && (
          <div className="flex flex-col items-start justify-center py-6 gap-2">
            <p className="body-strong text-[hsl(var(--forest-pulse))]">Importação concluída.</p>
            <p className="text-sm text-[hsl(var(--ink-primary))]">
              {uploadResult.created > 0 && (
                <>
                  <span className="num-mono body-strong">{uploadResult.created}</span> colaborador
                  {uploadResult.created === 1 ? "" : "es"} cadastrado
                  {uploadResult.created === 1 ? "" : "s"}.{" "}
                </>
              )}
              {uploadResult.updated > 0 && (
                <>
                  <span className="num-mono body-strong">{uploadResult.updated}</span> atualizado
                  {uploadResult.updated === 1 ? "" : "s"}.{" "}
                </>
              )}
              {(uploadResult.skippedErrors ?? 0) > 0 && (
                <span className="text-[hsl(var(--amber-state))]">
                  <span className="num-mono body-strong">{uploadResult.skippedErrors}</span> campo
                  {uploadResult.skippedErrors === 1 ? "" : "s"} com erro ignorado
                  {uploadResult.skippedErrors === 1 ? "" : "s"}.
                </span>
              )}
            </p>
          </div>
        )}

        <DialogFooter>
          {step === "errors" && (
            <>
              <Button variant="outline" onClick={resetState} data-testid="button-upload-try-again">
                Trocar arquivo
              </Button>
              <Button onClick={() => handleSubmit(true)} data-testid="button-ignore-errors-upload">
                {uploadMutation.isPending ? "Enviando…" : "Enviar ignorando inconsistências"}
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={resetState}>
                Fechar
              </Button>
              <Button onClick={() => handleSubmit()} data-testid="button-confirm-upload">
                {uploadMutation.isPending
                  ? "Enviando…"
                  : `Enviar ${parsedRows.length} linha${parsedRows.length === 1 ? "" : "s"}`}
              </Button>
            </>
          )}
          {step === "success" && (
            <Button onClick={() => handleClose(false)} data-testid="button-close-upload">
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
